/**
 * Reports API (Story 9-2: Investor ROI; Story 10-1: Reports dashboard).
 */
import api from "./api";
import type { InvestorRoiSummaryResponse, ReportsDashboardResponse } from "../types/models";

/**
 * Get reports dashboard metadata (categories and report items). Admin only.
 */
export async function getReportsDashboard(): Promise<ReportsDashboardResponse> {
  const response = await api.get<ReportsDashboardResponse>("/reports/dashboard");
  return response.data;
}

/**
 * Get investor ROI summary. Optional from/to for period; omit for all-time.
 */
export async function getInvestorRoiSummary(params?: {
  from_date?: string | null;
  to_date?: string | null;
}): Promise<InvestorRoiSummaryResponse> {
  const searchParams = new URLSearchParams();
  if (params?.from_date) searchParams.set("from_date", params.from_date);
  if (params?.to_date) searchParams.set("to_date", params.to_date);
  const qs = searchParams.toString();
  const url = qs ? `/reports/investors/roi?${qs}` : "/reports/investors/roi";
  const response = await api.get<InvestorRoiSummaryResponse>(url);
  return response.data;
}

/** Story 10-2: Cash Flow report response */
export interface CashFlowReportResponse {
  total_invested: number | string;
  total_spent: number | string;
  available_cash: number | string;
  chart_data: {
    month: string;
    cumulative_investments: number | string;
    cumulative_spending: number | string;
  }[];
}

/** Story 10-2: Cost Analysis report response */
export interface CategoryAmount {
  amount: number | string;
  percent: number;
}
export interface MonthlyByCategory {
  month: string;
  materials: number | string;
  equipment: number | string;
  operational: number | string;
  other: number | string;
}
export interface CostAnalysisReportResponse {
  by_category: Record<string, CategoryAmount>;
  monthly_by_category: MonthlyByCategory[];
  drill_down?: {
    top_items: { item_description: string; total: number | string; count: number }[];
    top_suppliers: { supplier_name: string; total: number | string; count: number }[];
    monthly_trend: { month: string; amount: number }[];
  } | null;
}

/** Story 10-2: Burn Rate report response */
export interface BurnRateReportResponse {
  monthly_burn_rate: number | string;
  runway_months: number | null;
  projected_depletion_date: string | null;
  trend: string;
  month_over_month_data: { month: string; burn_rate: number }[];
  warning_message: string | null;
}

/** Story 10-2: Get Cash Flow report */
export async function getCashFlowReport(params?: {
  from_date?: string | null;
  to_date?: string | null;
}): Promise<CashFlowReportResponse> {
  const searchParams = new URLSearchParams();
  if (params?.from_date) searchParams.set("from_date", params.from_date);
  if (params?.to_date) searchParams.set("to_date", params.to_date);
  const qs = searchParams.toString();
  const url = qs ? `/reports/financial/cash-flow?${qs}` : "/reports/financial/cash-flow";
  const response = await api.get<CashFlowReportResponse>(url);
  return response.data;
}

/** Story 10-2: Get Cost Analysis report */
export async function getCostAnalysisReport(params?: {
  from_date?: string | null;
  to_date?: string | null;
  category?: string | null;
}): Promise<CostAnalysisReportResponse> {
  const searchParams = new URLSearchParams();
  if (params?.from_date) searchParams.set("from_date", params.from_date);
  if (params?.to_date) searchParams.set("to_date", params.to_date);
  if (params?.category) searchParams.set("category", params.category);
  const qs = searchParams.toString();
  const url = qs ? `/reports/financial/cost-analysis?${qs}` : "/reports/financial/cost-analysis";
  const response = await api.get<CostAnalysisReportResponse>(url);
  return response.data;
}

/** Story 10-2: Get Burn Rate report */
export async function getBurnRateReport(params?: {
  from_date?: string | null;
  to_date?: string | null;
}): Promise<BurnRateReportResponse> {
  const searchParams = new URLSearchParams();
  if (params?.from_date) searchParams.set("from_date", params.from_date);
  if (params?.to_date) searchParams.set("to_date", params.to_date);
  const qs = searchParams.toString();
  const url = qs ? `/reports/financial/burn-rate?${qs}` : "/reports/financial/burn-rate";
  const response = await api.get<BurnRateReportResponse>(url);
  return response.data;
}

/** Story 10-3: Profit Margin report response */
export interface ProductMarginRow {
  product_name: string;
  units_sold: number;
  revenue: number | string;
  cost: number | string;
  margin: number | string;
  margin_percent: number | null;
  negative_margin: boolean;
}
export interface ProfitMarginReportResponse {
  total_revenue: number | string;
  total_cogs: number | string;
  gross_profit: number | string;
  gross_margin_percent: number | null;
  operating_expenses: number | string;
  net_margin: number | string;
  revenue_trend: { month: string; revenue: number | string }[];
  margin_trend: { month: string; margin_percent: number | null }[];
  product_breakdown: ProductMarginRow[];
  empty_message: string | null;
  previous_period_revenue?: number | string | null;
  previous_period_margin_percent?: number | null;
}

/** Story 10-3: Get Profit Margin report */
export async function getProfitMarginReport(params?: {
  from_date?: string | null;
  to_date?: string | null;
}): Promise<ProfitMarginReportResponse> {
  const searchParams = new URLSearchParams();
  if (params?.from_date) searchParams.set("from_date", params.from_date);
  if (params?.to_date) searchParams.set("to_date", params.to_date);
  const qs = searchParams.toString();
  const url = qs ? `/reports/financial/profit-margin?${qs}` : "/reports/financial/profit-margin";
  const response = await api.get<ProfitMarginReportResponse>(url);
  return response.data;
}

/** Story 10-4: Sales Velocity report types */
export interface TopProductRowSalesVelocity {
  product_name: string;
  units_sold: number;
  revenue: number | string;
  percent_of_total_sales: number;
  trend: string;
}
export interface SalesVelocityTrendPoint {
  period_label: string;
  units_sold: number;
}
export interface RevenueTrendPointTimeSeries {
  period_label: string;
  revenue: number | string;
}
export interface SlowMoverRow {
  product_name: string;
  current_quantity: number;
  units_sold_period: number;
  last_sold_at: string | null;
  declining: boolean;
}
export interface DailySalesPoint {
  date: string;
  revenue: number | string;
  order_count: number;
}
export interface SalesVelocityReportResponse {
  top_products_by_units: TopProductRowSalesVelocity[];
  top_products_by_revenue: TopProductRowSalesVelocity[];
  sales_velocity_trend: SalesVelocityTrendPoint[];
  revenue_trend: RevenueTrendPointTimeSeries[];
  daily_sales: DailySalesPoint[];
  total_revenue: number | string;
  average_daily_revenue: number | string;
  revenue_growth_percent: number | null;
  slow_movers: SlowMoverRow[];
  empty_message: string | null;
}

/** Story 10-4: Get Sales Velocity report */
export async function getSalesVelocityReport(params?: {
  from_date?: string | null;
  to_date?: string | null;
}): Promise<SalesVelocityReportResponse> {
  const searchParams = new URLSearchParams();
  if (params?.from_date) searchParams.set("from_date", params.from_date);
  if (params?.to_date) searchParams.set("to_date", params.to_date);
  const qs = searchParams.toString();
  const url = qs
    ? `/reports/operational/sales-velocity?${qs}`
    : "/reports/operational/sales-velocity";
  const response = await api.get<SalesVelocityReportResponse>(url);
  return response.data;
}

/** Story 10-5: Order Completion report types */
export interface FunnelStage {
  stage: string;
  count: number;
  drop_off_count: number;
  drop_off_rate: number | null;
}
export interface StageTiming {
  from_stage: string;
  to_stage: string;
  average_hours: number | null;
}
export interface CancellationBreakdownRow {
  stage_at_cancellation: string;
  reason: string;
  count: number;
}
export interface OrderCompletionReportResponse {
  total_created: number;
  orders_delivered: number;
  orders_delivered_percent: number;
  orders_cancelled: number;
  orders_cancelled_percent: number;
  orders_pending: number;
  funnel: FunnelStage[];
  average_time_per_stage: StageTiming[];
  cancellation_breakdown: CancellationBreakdownRow[];
  empty_message: string | null;
}

/** Story 10-5: Delivery Performance report types */
export interface DeliveryTimeBucket {
  bucket_label: string;
  count: number;
}
export interface DriverPerformanceRow {
  driver_id: string | null;
  driver_name: string | null;
  delivery_count: number;
  average_delivery_time_hours: number | null;
  issue_rate: number | null;
}
export interface DeliveryPerformanceReportResponse {
  avg_time_to_delivery_hours: number | null;
  median_delivery_time_hours: number | null;
  fastest_delivery_hours: number | null;
  slowest_delivery_hours: number | null;
  delivery_time_distribution: DeliveryTimeBucket[];
  stage_timing: StageTiming[];
  driver_performance: DriverPerformanceRow[];
  drill_down_orders: string[];
  empty_message: string | null;
}

/** Story 10-5: Get Order Completion report */
export async function getOrderCompletionReport(params?: {
  from_date?: string | null;
  to_date?: string | null;
}): Promise<OrderCompletionReportResponse> {
  const searchParams = new URLSearchParams();
  if (params?.from_date) searchParams.set("from_date", params.from_date);
  if (params?.to_date) searchParams.set("to_date", params.to_date);
  const qs = searchParams.toString();
  const url = qs
    ? `/reports/operational/order-completion?${qs}`
    : "/reports/operational/order-completion";
  const response = await api.get<OrderCompletionReportResponse>(url);
  return response.data;
}

/** Story 10-5: Get Delivery Performance report */
export async function getDeliveryPerformanceReport(params?: {
  from_date?: string | null;
  to_date?: string | null;
}): Promise<DeliveryPerformanceReportResponse> {
  const searchParams = new URLSearchParams();
  if (params?.from_date) searchParams.set("from_date", params.from_date);
  if (params?.to_date) searchParams.set("to_date", params.to_date);
  const qs = searchParams.toString();
  const url = qs
    ? `/reports/operational/delivery-performance?${qs}`
    : "/reports/operational/delivery-performance";
  const response = await api.get<DeliveryPerformanceReportResponse>(url);
  return response.data;
}

/** Story 10-6: Top Customers report types */
export interface TopCustomerRow {
  customer_id: string;
  name: string;
  total_orders: number;
  total_revenue: number | string;
  avg_order_value: number | string;
  last_order_date: string | null;
  first_order_date: string | null;
}
export interface VipSegmentRow {
  customer_id: string;
  name: string;
  total_revenue: number | string;
}
export interface OrderHistoryEntry {
  order_id: string;
  order_number: string;
  order_date: string;
  amount: number | string;
  status: string;
}
export interface FavoriteProductRow {
  product_name: string;
  total_quantity: number;
}
export interface RevenueTrendPointCustomer {
  period_label: string;
  revenue: number | string;
}
export interface TopCustomerDetail {
  order_history: OrderHistoryEntry[];
  favorite_products: FavoriteProductRow[];
  order_frequency_pattern: string | null;
  revenue_trend: RevenueTrendPointCustomer[];
}
export interface TopCustomersReportResponse {
  top_customers: TopCustomerRow[];
  vip_segment: VipSegmentRow[];
  customer_detail: TopCustomerDetail | null;
  empty_message: string | null;
}

/** Story 10-6: Get Top Customers report */
export async function getTopCustomersReport(params?: {
  from_date?: string | null;
  to_date?: string | null;
  sort_by?: "revenue" | "order_frequency" | "avg_order_value";
  customer_id?: string | null;
}): Promise<TopCustomersReportResponse> {
  const searchParams = new URLSearchParams();
  if (params?.from_date) searchParams.set("from_date", params.from_date);
  if (params?.to_date) searchParams.set("to_date", params.to_date);
  if (params?.sort_by) searchParams.set("sort_by", params.sort_by);
  if (params?.customer_id) searchParams.set("customer_id", params.customer_id);
  const qs = searchParams.toString();
  const url = qs ? `/reports/customers/top?${qs}` : "/reports/customers/top";
  const response = await api.get<TopCustomersReportResponse>(url);
  return response.data;
}

/** Story 10-6: Cancellation Patterns report types */
export interface HighCancellationCustomerRow {
  customer_id: string;
  name: string;
  total_orders: number;
  cancelled_orders: number;
  cancellation_rate_pct: number;
  last_cancellation_reason: string | null;
}
export interface CancellationReasonRow {
  reason: string;
  count: number;
}
export interface TimePatternRow {
  bucket_label: string;
  count: number;
}
export interface RiskSegmentRow {
  customer_id: string;
  name: string;
  cancellation_rate_pct: number;
  cancelled_orders: number;
  recent_cancellations: number;
  flagged: boolean;
}
export interface CancelledOrderWithReason {
  order_id: string;
  order_number: string;
  cancelled_date: string;
  reason: string;
}
export interface CancellationCustomerDetail {
  cancelled_orders: CancelledOrderWithReason[];
  pattern_analysis: string | null;
  suggested_action: string | null;
}
export interface CancellationPatternsReportResponse {
  overall_cancellation_rate: number;
  high_cancellation_customers: HighCancellationCustomerRow[];
  common_cancellation_reasons: CancellationReasonRow[];
  time_patterns: TimePatternRow[];
  risk_segment: RiskSegmentRow[];
  customer_detail: CancellationCustomerDetail | null;
  empty_message: string | null;
}

/** Story 10-6: Get Cancellation Patterns report */
export async function getCancellationPatternsReport(params?: {
  from_date?: string | null;
  to_date?: string | null;
  customer_id?: string | null;
}): Promise<CancellationPatternsReportResponse> {
  const searchParams = new URLSearchParams();
  if (params?.from_date) searchParams.set("from_date", params.from_date);
  if (params?.to_date) searchParams.set("to_date", params.to_date);
  if (params?.customer_id) searchParams.set("customer_id", params.customer_id);
  const qs = searchParams.toString();
  const url = qs
    ? `/reports/customers/cancellation-patterns?${qs}`
    : "/reports/customers/cancellation-patterns";
  const response = await api.get<CancellationPatternsReportResponse>(url);
  return response.data;
}

/** Story 10-7: Inventory Health report types */
export interface HealthOverview {
  total_skus: number;
  healthy_count: number;
  low_stock_count: number;
  out_of_stock_count: number;
  overstocked_count: number;
}
export interface LowStockItem {
  item_id: string;
  name: string;
  current_quantity: number;
  reorder_threshold: number;
  days_until_stockout: number | null;
  recommended_reorder_quantity: number;
}
export interface OverstockedItem {
  item_id: string;
  name: string;
  current_quantity: number;
  monthly_sales_avg: number | string;
  months_of_supply: number | null;
  capital_tied_up: number | string;
}
export interface ReorderRecommendationRow {
  item_id: string;
  name: string;
  urgency: string;
  recommended_quantity: number;
  estimated_cost: number | string;
}
export interface ReorderRecommendation {
  critical_now: ReorderRecommendationRow[];
  reorder_soon_within_2_weeks: ReorderRecommendationRow[];
  optimal_quantities: ReorderRecommendationRow[];
  estimated_cost: number | string;
}
export interface InventoryTurnover {
  turnover_ratio: number | null;
  days_inventory_outstanding: number | null;
  industry_benchmark_available: boolean;
}
export interface StockLevelHistoryPoint {
  date: string;
  quantity: number;
}
export interface ReorderHistoryEntry {
  purchase_date: string;
  quantity: number | string;
  total: number | string;
  notes: string | null;
}
export interface ItemDetail {
  stock_level_history: StockLevelHistoryPoint[];
  sales_velocity_trend: { period_label: string; units_sold?: number }[];
  stockout_history: string[];
  reorder_history: ReorderHistoryEntry[];
}
export interface InventoryHealthReportResponse {
  health_overview: HealthOverview;
  low_stock_items: LowStockItem[];
  overstocked_items: OverstockedItem[];
  reorder_recommendations: ReorderRecommendation;
  inventory_turnover: InventoryTurnover;
  item_detail: ItemDetail | null;
  empty_message: string | null;
}

/** Story 10-7: Get Inventory Health report */
export async function getInventoryHealthReport(params?: {
  from_date?: string | null;
  to_date?: string | null;
  item_id?: string | null;
}): Promise<InventoryHealthReportResponse> {
  const searchParams = new URLSearchParams();
  if (params?.from_date) searchParams.set("from_date", params.from_date);
  if (params?.to_date) searchParams.set("to_date", params.to_date);
  if (params?.item_id) searchParams.set("item_id", params.item_id);
  const qs = searchParams.toString();
  const url = qs ? `/reports/inventory/health?${qs}` : "/reports/inventory/health";
  const response = await api.get<InventoryHealthReportResponse>(url);
  return response.data;
}

/** Story 10-8: Purchase Patterns report types */
export interface PurchaseOverview {
  total_spend: number | string;
  purchase_count: number;
}
export interface PurchaseTopItemRow {
  item_description: string;
  total_spend: number | string;
  purchase_count: number;
}
export interface PurchaseTopSupplierRow {
  supplier_id: string;
  supplier_name: string;
  total_spend: number | string;
  purchase_count: number;
}
export interface SupplierAnalysisRow {
  supplier_id: string;
  supplier_name: string;
  total_spend: number | string;
  purchase_count: number;
  avg_purchase_amount: number | string;
  items_purchased: string[];
  pct_of_total: number;
}
export interface FrequentlyPurchasedItem {
  item_description: string;
  purchase_count: number;
  total_spend: number | string;
}
export interface ConsolidationOpportunity {
  item_description: string;
  supplier_count: number;
  suppliers: string[];
  total_spend: number | string;
}
export interface FrequencyPattern {
  pattern_label: string;
  purchase_count: number;
  avg_days_between: number | null;
}
export interface OptimizationSection {
  frequently_purchased_items: FrequentlyPurchasedItem[];
  consolidation_opportunities: ConsolidationOpportunity[];
  frequency_patterns: FrequencyPattern[];
}
export interface DayOfWeekPattern {
  day_name: string;
  purchase_count: number;
}
export interface WeekOfMonthPattern {
  week_label: string;
  purchase_count: number;
}
export interface EmergencyVsPlanned {
  emergency_count: number;
  planned_count: number;
  emergency_pct: number;
  planned_pct: number;
}
export interface TimingPatterns {
  by_day_of_week: DayOfWeekPattern[];
  by_week_of_month: WeekOfMonthPattern[];
  emergency_vs_planned: EmergencyVsPlanned;
  seasonal_patterns: Record<string, number> | null;
}
export interface PurchaseHistoryEntry {
  purchase_id: string;
  purchase_date: string;
  item_description: string;
  quantity: number | string;
  unit_cost: number | string;
  total: number | string;
  notes: string | null;
}
export interface PriceTrendPoint {
  period_label: string;
  avg_unit_cost: number | string;
  purchase_count: number;
}
export interface SupplierContactInfo {
  company_name: string;
  contact_person: string | null;
  phone: string;
  email: string | null;
  address: string | null;
}
export interface SupplierDetail {
  purchase_history: PurchaseHistoryEntry[];
  items_purchased: string[];
  price_trends: PriceTrendPoint[];
  contact_info: SupplierContactInfo;
}
export interface PurchasePatternsReportResponse {
  overview: PurchaseOverview;
  top_items_by_spend: PurchaseTopItemRow[];
  top_items_by_frequency: PurchaseTopItemRow[];
  top_suppliers: PurchaseTopSupplierRow[];
  supplier_analysis: SupplierAnalysisRow[];
  optimization: OptimizationSection;
  timing_patterns: TimingPatterns;
  supplier_detail: SupplierDetail | null;
  empty_message: string | null;
}

/** Story 10-8: Get Purchase Patterns report */
export async function getPurchasePatternsReport(params?: {
  from_date?: string | null;
  to_date?: string | null;
  supplier_id?: string | null;
}): Promise<PurchasePatternsReportResponse> {
  const searchParams = new URLSearchParams();
  if (params?.from_date) searchParams.set("from_date", params.from_date);
  if (params?.to_date) searchParams.set("to_date", params.to_date);
  if (params?.supplier_id) searchParams.set("supplier_id", params.supplier_id);
  const qs = searchParams.toString();
  const url = qs
    ? `/reports/inventory/purchase-patterns?${qs}`
    : "/reports/inventory/purchase-patterns";
  const response = await api.get<PurchasePatternsReportResponse>(url);
  return response.data;
}

/** Story 10-9: KPI Dashboard types */
export interface OrdersKPISection {
  today: {
    count: number;
    value: number;
    change: number;
  };
  this_week: {
    count: number;
    value: number;
    change_pct: number | null;
  };
  this_month: {
    count: number;
    value: number;
    change_pct: number | null;
  };
}
export interface RevenueKPISection {
  this_month: number;
  change_pct: number | null;
}
export interface CustomersKPISection {
  total: number;
  new_this_month: number;
  repeat_this_month: number;
}
export interface OperationsKPISection {
  pending_validation: number;
  in_delivery: number;
  delivery_success_rate: number | null;
  avg_delivery_hours: number | null;
}
export interface KPIDashboardResponse {
  orders: OrdersKPISection;
  revenue: RevenueKPISection;
  customers: CustomersKPISection;
  operations: OperationsKPISection;
}

/** Story 10-9: Get KPI Dashboard */
export async function getKPIDashboard(): Promise<KPIDashboardResponse> {
  const response = await api.get<KPIDashboardResponse>("/reports/kpi");
  return response.data;
}

/** Story 11.6-4: Receivables report types (flat unpaid-orders list) */
export interface ReceivablesRow {
  order_id: string;
  order_number: string;
  customer_name: string;
  total_amount: number | string;
  order_date: string;
  payment_due_date: string | null;
  days_overdue: number;
  status: string;
}
export interface ReceivablesReportResponse {
  total_outstanding: number | string;
  count: number;
  rows: ReceivablesRow[];
}

/** Story 11.6-4: Get Receivables report */
export async function getReceivablesReport(params?: {
  overdue_only?: boolean;
  customer_id?: string | null;
  from_date?: string | null;
  to_date?: string | null;
  sort_by?: "amount" | "due_date" | "days_overdue";
}): Promise<ReceivablesReportResponse> {
  const searchParams = new URLSearchParams();
  if (params?.overdue_only) searchParams.set("overdue_only", "true");
  if (params?.customer_id) searchParams.set("customer_id", params.customer_id);
  if (params?.from_date) searchParams.set("from_date", params.from_date);
  if (params?.to_date) searchParams.set("to_date", params.to_date);
  if (params?.sort_by) searchParams.set("sort_by", params.sort_by);
  const qs = searchParams.toString();
  const url = qs ? `/reports/receivables?${qs}` : "/reports/receivables";
  const response = await api.get<ReceivablesReportResponse>(url);
  return response.data;
}

/** Story 11.9-1: Revenue by Period report */
export type PeriodGranularity = "day" | "week" | "month" | "year";

export interface PeriodRevenueRow {
  period_start: string;
  period_end: string;
  period_label: string;
  order_count: number;
  billed_revenue: number | string;
  collected_revenue: number | string;
}

export interface RevenueByPeriodReportResponse {
  from_date: string;
  to_date: string;
  granularity: PeriodGranularity;
  rows: PeriodRevenueRow[];
  totals: PeriodRevenueRow;
}

export interface PeriodExpenseRow {
  period_start: string;
  period_end: string;
  period_label: string;
  purchase_count: number;
  total_spend: number | string;
}

export interface ExpensesByPeriodReportResponse {
  from_date: string;
  to_date: string;
  granularity: PeriodGranularity;
  rows: PeriodExpenseRow[];
  totals: PeriodExpenseRow;
}

export async function getRevenueByPeriodReport(params: {
  from_date: string;
  to_date: string;
  granularity?: PeriodGranularity;
}): Promise<RevenueByPeriodReportResponse> {
  const searchParams = new URLSearchParams({
    from_date: params.from_date,
    to_date: params.to_date,
    granularity: params.granularity ?? "day",
  });
  const response = await api.get<RevenueByPeriodReportResponse>(
    `/reports/financial/revenue-by-period?${searchParams.toString()}`
  );
  return response.data;
}

export async function getExpensesByPeriodReport(params: {
  from_date: string;
  to_date: string;
  granularity?: PeriodGranularity;
}): Promise<ExpensesByPeriodReportResponse> {
  const searchParams = new URLSearchParams({
    from_date: params.from_date,
    to_date: params.to_date,
    granularity: params.granularity ?? "day",
  });
  const response = await api.get<ExpensesByPeriodReportResponse>(
    `/reports/financial/expenses-by-period?${searchParams.toString()}`
  );
  return response.data;
}
